import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Alert,
  Button,
  ButtonGroup,
  Row,
  Col,
  Spinner
} from "reactstrap";
import request from "common/request";

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  availableCoupons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      code: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ).isRequired,
  attachedCoupons: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      discount_display: PropTypes.string
    })
  ),
  requestPath: PropTypes.string.isRequired,
  campaignTypeSnake: PropTypes.string.isRequired
};

const AddCouponModal = ({
  isOpen,
  onClose,
  onSuccess,
  availableCoupons,
  attachedCoupons = [],
  requestPath,
  campaignTypeSnake
}) => {
  const [selectedCouponId, setSelectedCouponId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    const couponCode = availableCoupons.find(c => String(c.id) === String(selectedCouponId))?.code;
    setIsProcessing(true);
    setError(null);
    try {
      await request.post(requestPath, {
        [campaignTypeSnake]: { coupon: { id: selectedCouponId } }
      });
      onSuccess(`We have applied the following coupon to this campaign: ${couponCode}`);
    } catch (e) {
      setIsProcessing(false);
      setError(e?.response?.data?.error || "An error occurred. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={isProcessing ? undefined : onClose}
      fade={false}
      size="lg"
      className="account-app modal-dialog-centered"
    >
      <ModalHeader toggle={isProcessing ? undefined : onClose} className="p-3">
        Add coupon to campaign
      </ModalHeader>
      <ModalBody className="px-4 pt-2 pb-3">
        {attachedCoupons.length > 0 && (
          <Alert color="info" className="my-2">
            The following coupons are currently set to recur for this campaign. Adding a new coupon
            will remove these coupons from all future recurring payments:
            {attachedCoupons.map(coupon => (
              <Row key={coupon.code}>
                <Col xs="12">
                  <span className="font-weight-bold">Coupon Code:</span> {coupon.code},{" "}
                  <span className="font-weight-bold">Discount:</span> {coupon.discount_display}
                </Col>
              </Row>
            ))}
          </Alert>
        )}
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}
        {isProcessing ? (
          <div className="text-center py-3">
            <Spinner color="primary" className="mb-3" />
            <p>Adding coupon...</p>
          </div>
        ) : (
          <>
            <Label for="couponSelect">Which coupon?</Label>
            <Input
              type="select"
              id="couponSelect"
              value={selectedCouponId}
              onChange={e => setSelectedCouponId(e.target.value)}
            >
              <option value="">Select coupon...</option>
              {availableCoupons.map(coupon => (
                <option key={coupon.id} value={coupon.id}>
                  {coupon.code}
                </option>
              ))}
            </Input>
          </>
        )}
      </ModalBody>
      {!isProcessing && (
        <ModalFooter>
          <ButtonGroup>
            <Button
              color="primary"
              className="btn-sharp mx-1"
              onClick={handleSubmit}
              disabled={!selectedCouponId}
            >
              Apply Coupon
            </Button>
            <Button color="secondary" className="btn-sharp mx-1" onClick={onClose}>
              Cancel
            </Button>
          </ButtonGroup>
        </ModalFooter>
      )}
    </Modal>
  );
};

AddCouponModal.propTypes = propTypes;

export default AddCouponModal;
